import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Mail, Printer, Star, Zap } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { toast } from 'sonner'
import { driver, useStations } from '@/api/driver'
import { isMissing, receipts } from '@/api/proposed'
import type { DriverSession } from '@/api/types'
import { EmptyState, ErrorState, LoadingBlock } from '@/components/common'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth'
import { dateTime, duration, kwh, npr } from '@/lib/format'
import { useI18n } from '@/lib/i18n'
import { errorText } from '@/lib/utils'
import { ReviewDialog } from './StationExtras'
import { ReceiptPayment } from './ReceiptPayment'

/** One session, by id: the API's D14 endpoint, or found in recent history until then. */
async function findSession(id: number): Promise<DriverSession | null> {
  try {
    return await receipts.session(id)
  } catch (e) {
    if (!isMissing(e)) throw e
  }
  const h = await driver.history({ page: 1, per_page: 100 })
  return h.sessions.find((s) => s.id === id) ?? null
}

export function ReceiptPage() {
  const { id } = useParams()
  const { t } = useI18n()
  const { user } = useAuth()
  const q = useQuery({ queryKey: ['receipt', id], queryFn: () => findSession(Number(id)) })
  const stations = useStations()
  const [rating, setRating] = useState(false)
  const [sending, setSending] = useState(false)
  if (q.isLoading) return <LoadingBlock rows={5} />
  if (q.error) return <ErrorState error={q.error} onRetry={() => q.refetch()} />
  const s = q.data
  if (!s) return <EmptyState title={t('Receipt not found')} body={t('Older receipts are in History.')} action={<Link to="/history" className="underline">{t('History')}</Link>} />
  const station = stations.data?.stations.find((x) => x.id === s.station_id)
  const rate = s.cost_breakdown?.rate_per_kwh
  const email = async () => {
    setSending(true)
    try {
      await receipts.email(s.id)
      toast.success(t('Sent to {email}', { email: user?.email ?? '' }))
    } catch (e) {
      toast.error(isMissing(e) ? t('Emailing receipts is coming soon. Use Print to save a PDF.') : errorText(e))
    } finally {
      setSending(false)
    }
  }
  const rows: [string, string][] = [
    [t('Date'), dateTime(s.start_time)],
    [t('Station'), s.station_name ?? '–'],
    [t('Operator'), station?.cpo_name ?? '–'],
    [t('Charger'), `${s.charger_id} · ${t('connector {n}', { n: s.connector_id })}${s.connector?.connector_type ? ` · ${s.connector.connector_type}` : ''}`],
    [t('Duration'), duration(s.start_time, s.stop_time ?? undefined)],
    [t('Energy'), kwh(s.energy_kwh)],
    ...(rate != null ? [[t('Rate'), `${npr(rate)} / kWh`] as [string, string]] : []),
    ...(s.cost_breakdown?.plan_name ? [[t('Price plan'), String(s.cost_breakdown.plan_name)] as [string, string]] : []),
    [t('Card'), s.id_tag ?? '–'],
  ]
  return (
    <div className="mx-auto max-w-md">
      <div className="mb-4 flex items-center justify-between print:hidden">
        <Link to="/history" className="-ml-2 flex items-center gap-1 rounded-lg px-2 py-1 text-sm text-text-2 hover:bg-surface-3"><ArrowLeft className="size-4" /> {t('History')}</Link>
        <div className="flex gap-1">
          {user?.email && <Button size="sm" variant="ghost" loading={sending} onClick={email}><Mail /> {t('Email me')}</Button>}
          <Button size="sm" variant="ghost" onClick={() => window.print()}><Printer /> {t('Print / PDF')}</Button>
        </div>
      </div>
      <article className="rounded-2xl border border-border bg-surface p-6 print:border-0 print:p-0">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 font-semibold"><span className="grid size-8 place-items-center rounded-lg bg-ink text-brand"><Zap className="size-4 fill-current" /></span>Saadhan Charge</span>
          <span className="text-xs text-text-2">{t('Receipt #{id}', { id: s.id })}</span>
        </div>
        <p className="mt-6 text-sm text-text-2">{t('Charging cost')}</p>
        <p className="tabular text-4xl font-bold">{npr(s.cost)}</p>
        <ReceiptPayment s={s} />
        <dl className="mt-6 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2.5 border-t border-dashed border-border pt-4 text-sm">
          {rows.map(([k, v]) => [<dt key={k} className="text-text-2">{k}</dt>, <dd key={`${k}v`} className="text-right">{v}</dd>])}
        </dl>
        {s.status !== 'completed' && <p className="mt-4 rounded-lg bg-warning-soft p-2.5 text-[13px] text-warning">{s.status === 'active' ? t('Still charging; the total will change.') : t('This charge ended early: {reason}', { reason: s.error_reason ?? s.status })}</p>}
        <p className="mt-6 text-xs text-text-2">{t('Questions about this charge? Write to us from Help with the receipt number.')}</p>
      </article>
      {s.station_id && s.status !== 'active' && (
        <Button variant="outline" className="mt-4 w-full print:hidden" onClick={() => setRating(true)}><Star /> {t('Rate this station')}</Button>
      )}
      {rating && s.station_id && <ReviewDialog stationId={s.station_id} stationName={s.station_name ?? ''} sessionId={s.id} onClose={() => setRating(false)} />}
    </div>
  )
}
